/**
 * useMembersState - Custom hook for Newsletter Members module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

export const useMembersState = ({ setError, setSuccess }) => {
  // Members state
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Fetch newsletter members
  const fetchMembers = useCallback(async () => {
    setLoadingMembers(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/members`);
      setMembers(response.data.members || []);
    } catch (err) {
      setError('Failed to fetch members: ' + err.message);
    } finally {
      setLoadingMembers(false);
    }
  }, [setError]);

  // Delete a member
  const deleteMember = useCallback(async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this member?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/members/${memberId}`);
      setSuccess('Member deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchMembers();
    } catch (err) {
      setError('Failed to delete member: ' + err.message);
    }
  }, [fetchMembers, setError, setSuccess]);

  // Export members to CSV
  const exportMembers = useCallback(() => {
    const csv = [
      ['Name', 'Email', 'Date Joined', 'Status'].join(','),
      ...members.map(m => [
        m.firstName,
        m.email,
        new Date(m.timestamp).toLocaleDateString(),
        m.status || 'active'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `golf-nuts-members-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  }, [members]);

  return {
    // State
    members,
    loadingMembers,

    // Setters
    setMembers,

    // Actions
    fetchMembers,
    deleteMember,
    exportMembers,
  };
};

export default useMembersState;
